import { useEffect, useState } from "react";
import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { db, SUPER_ADMIN_EMAIL } from "../../firebase/config";
import { useAuth } from "../../contexts/AuthContext";

const ROLES = [
  { value: "admin", label: "Admin — แก้ Settings ได้" },
  { value: "editor", label: "Editor — แก้เฉพาะเนื้อหา" },
];

export default function AdminsAdmin() {
  const { user, isSuperAdmin } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);

  const [newEmail, setNewEmail] = useState("");
  const [newDisplayName, setNewDisplayName] = useState("");
  const [newRole, setNewRole] = useState("editor");
  const [formError, setFormError] = useState("");
  const [formSuccess, setFormSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "admins"),
      (snap) => {
        const rows = snap.docs.map((d) => ({ email: d.id, ...d.data() }));
        setAdmins(rows);
        setLoading(false);
      },
      () => setLoading(false)
    );
    return unsub;
  }, []);

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    setFormError("");
    setFormSuccess("");

    const email = newEmail.trim().toLowerCase();
    const displayName = newDisplayName.trim();

    if (!email || !email.includes("@")) {
      setFormError("รูปแบบอีเมลไม่ถูกต้อง");
      return;
    }
    if (email === SUPER_ADMIN_EMAIL.toLowerCase()) {
      setFormError("อีเมลนี้เป็น Super Admin อยู่แล้ว ไม่ต้องเพิ่มซ้ำ");
      return;
    }
    if (admins.some((a) => a.email.toLowerCase() === email)) {
      setFormError("อีเมลนี้เป็น admin อยู่แล้ว");
      return;
    }
    if (!ROLES.some((r) => r.value === newRole)) {
      setFormError("Role ไม่ถูกต้อง");
      return;
    }

    setSubmitting(true);
    try {
      await setDoc(doc(db, "admins", email), {
        displayName: displayName || null,
        role: newRole,
        addedBy: user?.email || "unknown",
        addedAt: serverTimestamp(),
      });
      setFormSuccess(`เพิ่ม ${email} เป็น ${newRole} สำเร็จ`);
      setNewEmail("");
      setNewDisplayName("");
      setNewRole("editor");
    } catch (err) {
      setFormError(`เพิ่มไม่สำเร็จ: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleChangeRole = async (email, role) => {
    setFormError("");
    try {
      await updateDoc(doc(db, "admins", email), { role });
      setFormSuccess(`เปลี่ยน role ของ ${email} เป็น ${role} สำเร็จ`);
      // Auto-clear success message หลัง 3 วินาที — ไม่ค้าง UI
      setTimeout(() => setFormSuccess(""), 3000);
    } catch (err) {
      setFormError(`เปลี่ยน role ไม่สำเร็จ: ${err.message}`);
    }
  };

  const handleRemoveAdmin = async (email) => {
    if (email.toLowerCase() === SUPER_ADMIN_EMAIL.toLowerCase()) {
      alert("ลบ super admin ไม่ได้");
      return;
    }
    if (!window.confirm(`ลบ admin "${email}" ใช่หรือไม่?`)) return;
    try {
      await deleteDoc(doc(db, "admins", email));
    } catch (err) {
      alert(`ลบไม่สำเร็จ: ${err.message}`);
    }
  };

  if (!isSuperAdmin) {
    return (
      <div className="alert alert-warning">
        หน้านี้เข้าได้เฉพาะ Super Admin เท่านั้น
      </div>
    );
  }

  return (
    <div>
      <h3 className="mb-4">จัดการผู้ดูแลระบบ</h3>

      <div className="card mb-4">
        <div className="card-body">
          <h5 className="card-title mb-3">เพิ่ม Admin ใหม่</h5>

          {formError && <div className="alert alert-danger py-2">{formError}</div>}
          {formSuccess && <div className="alert alert-success py-2">{formSuccess}</div>}

          <form onSubmit={handleAddAdmin} className="row g-2">
            <div className="col-md-4">
              <input
                type="email"
                className="form-control"
                placeholder="email@example.com"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                required
              />
            </div>
            <div className="col-md-3">
              <input
                type="text"
                className="form-control"
                placeholder="ชื่อแสดง (optional)"
                value={newDisplayName}
                onChange={(e) => setNewDisplayName(e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <select
                className="form-select"
                value={newRole}
                onChange={(e) => setNewRole(e.target.value)}
              >
                {ROLES.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <button
                type="submit"
                className="btn btn-primary w-100"
                disabled={submitting}
              >
                {submitting ? "..." : "เพิ่ม"}
              </button>
            </div>
          </form>

          <small className="text-muted d-block mt-2">
            * ผู้ใช้ต้องมีบัญชี Firebase Auth (email/password) อยู่ก่อน จึงจะ
            login เข้าระบบได้หลังถูกเพิ่มชื่อ
          </small>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <h5 className="card-title mb-3">รายชื่อ Admin ปัจจุบัน</h5>
          {loading ? (
            <div className="spinner-border spinner-border-sm" />
          ) : (
            <table className="table align-middle">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>ชื่อ</th>
                  <th style={{ width: 200 }}>Role</th>
                  <th>เพิ่มโดย</th>
                  <th style={{ width: 100 }}></th>
                </tr>
              </thead>
              <tbody>
                <tr className="table-warning">
                  <td>
                    <strong>{SUPER_ADMIN_EMAIL}</strong>
                  </td>
                  <td>—</td>
                  <td>
                    <span className="badge bg-warning text-dark">Super</span>
                  </td>
                  <td>system</td>
                  <td><span className="text-muted small">ลบไม่ได้</span></td>
                </tr>
                {admins.map((a) => {
                  const currentRole = a.role || "editor";
                  return (
                    <tr key={a.email}>
                      <td>{a.email}</td>
                      <td>{a.displayName || "—"}</td>
                      <td>
                        <select
                          className="form-select form-select-sm"
                          value={currentRole}
                          onChange={(e) => handleChangeRole(a.email, e.target.value)}
                        >
                          {ROLES.map((r) => (
                            <option key={r.value} value={r.value}>
                              {r.value}
                            </option>
                          ))}
                        </select>
                        {!a.role && (
                          <small className="text-muted">* default (ยังไม่ได้บันทึก)</small>
                        )}
                      </td>
                      <td className="small text-muted">{a.addedBy || "—"}</td>
                      <td>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleRemoveAdmin(a.email)}
                        >
                          ลบ
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {admins.length === 0 && (
                  <tr>
                    <td colSpan="5" className="text-center text-muted">
                      ยังไม่มี admin เพิ่มเติม
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
